import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";

export default function CategoriesPage() {
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      const snap = await getDocs(collection(db, "categories"));
      setCategories(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }
    fetchCategories();
  }, []);

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return alert("Nom de catégorie requis");
    setLoading(true);
    try {
      const ref = await addDoc(collection(db, "categories"), { name });
      setCategories((c) => [...c, { id: ref.id, name }]);
      setNewCategory("");
    } catch {
      alert("Erreur création catégorie");
    }
    setLoading(false);
  };

  const updateCategory = async (id, newName) => {
    const name = newName.trim();
    if (!name) return alert("Nom de catégorie requis");
    setSavingId(id);
    try {
      const ref = doc(db, "categories", id);
      await updateDoc(ref, { name });
      setCategories((cats) =>
        cats.map((c) => (c.id === id ? { ...c, name } : c))
      );
    } catch {
      alert("Erreur mise à jour");
    }
    setSavingId(null);
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Confirmez-vous la suppression de cette catégorie ?")) return;
    setSavingId(id);
    try {
      await deleteDoc(doc(db, "categories", id));
      setCategories((cats) => cats.filter((c) => c.id !== id));
    } catch {
      alert("Erreur suppression catégorie");
    }
    setSavingId(null);
  };

  return (
    <PageTemplate
      loading={loading}
      content={
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Total</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">{categories.length}</p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Statut</p>
              <p className="text-lg font-semibold text-gray-900 mt-0.5">
                {categories.length > 0 ? "Actif" : "Vide"}
              </p>
            </div>
          </div>

          {/* Add */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-3 py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-900">Ajouter une catégorie</span>
            </div>
            <div className="p-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Nom de la catégorie"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newCategory.trim()) addCategory();
                  }}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:opacity-50"
                  disabled={loading}
                />
                <button
                  onClick={addCategory}
                  disabled={loading || !newCategory.trim()}
                  className="px-4 py-1.5 bg-[#801111] text-white rounded-md text-xs font-medium hover:bg-[#5a0c0c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
            </div>
          </div>

          {/* List */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="px-3 py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-900">Liste des catégories</span>
            </div>
            {categories.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500">Aucune catégorie</p>
                <p className="text-xs text-gray-400 mt-1">Commencez par créer votre première catégorie</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {categories.map(({ id, name }) => (
                  <EditableCategoryItem
                    key={id}
                    id={id}
                    name={name}
                    saving={savingId === id}
                    onUpdate={updateCategory}
                    onDelete={deleteCategory}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}

function EditableCategoryItem({ id, name, saving, onUpdate, onDelete }) {
  const [editName, setEditName] = useState(name);
  const [editing, setEditing] = useState(false);

  const save = () => {
    if (editName.trim() !== name) {
      onUpdate(id, editName);
    }
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 group">
      {editing ? (
        <>
          <input
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={saving}
            className="flex-1 border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") { setEditName(name); setEditing(false); }
            }}
            autoFocus
          />
          <button
            onClick={save}
            disabled={saving}
            className="text-xs font-medium text-green-600 hover:text-green-800 disabled:opacity-50"
          >
            OK
          </button>
          <button
            onClick={() => { setEditName(name); setEditing(false); }}
            disabled={saving}
            className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            Annuler
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 text-sm text-gray-900">{name}</span>
          <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setEditing(true)}
              disabled={saving}
              className="text-xs font-medium text-[#801111] hover:text-[#5a0c0c] disabled:opacity-50"
            >
              Modifier
            </button>
            <button
              onClick={() => onDelete(id)}
              disabled={saving}
              className="text-xs font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
            >
              Supprimer
            </button>
          </div>
        </>
      )}
    </div>
  );
}
